import { useCallback, useEffect, useRef } from 'react';

/**
 * Объектные адреса для превью — с уборкой за собой.
 *
 * `URL.createObjectURL` держит файл в памяти вкладки, пока адрес не отозвали, и
 * до объединения дропзон за этим следил только один из трёх вариантов. Хуже
 * всего было в легаси-дропзоне: она звала `createObjectURL` прямо в рендере, то
 * есть выдавала новый адрес тем же самым файлам на каждый проход и не отзывала
 * ни одного.
 *
 * Здесь адрес заводится один раз на файл, а отзывается в двух случаях: когда
 * файл убрали из поля и когда поле размонтировали.
 */
export function useObjectUrls(files: File[]) {
  const urlsRef = useRef(new Map<File, string>());

  const resolveUrl = useCallback((file: File) => {
    const urls = urlsRef.current;
    let url = urls.get(file);

    if (!url) {
      url = URL.createObjectURL(file);
      urls.set(file, url);
    }

    return url;
  }, []);

  useEffect(() => {
    const urls = urlsRef.current;

    urls.forEach((url, file) => {
      if (files.includes(file)) return;

      URL.revokeObjectURL(url);
      urls.delete(file);
    });
  }, [files]);

  useEffect(() => {
    const urls = urlsRef.current;

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  return resolveUrl;
}
